'use client'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { buildRegionUrl, buildUrl } from '@/shared/lib/utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion'
import type { Group, TripType, GroupStatus, GroupRetention } from '@/features/stm/lib/types'
import { useStmDeleteGroup, useStmUpdateGroup } from '@/features/stm/hooks/use-stm-groups-blocks'
import { useStmWeeksWithStats } from '@/features/stm/hooks/use-stm-weeks'
import { doesOverlap } from '@/features/stm/lib/date-utils'
import StickySaveBar from '@/shared/components/sticky-save-bar'
import { GroupLeaderCard, type NewContactState } from './GroupLeaderCard'
import { GroupOverallStatusCard } from './GroupOverallStatusCard'
import { GroupNotesCard, type TaggedVolunteer } from './GroupNotesCard'
import { GroupChecklistCard } from './GroupChecklistCard'
import { toast } from 'sonner'
import { useSession } from '@/features/auth/context/session.context'
import { useRegion } from '@/shared/hooks/use-region'
import { createVolunteer } from '@/features/volunteers/api/volunteers.api'
import { updateUser } from '@/features/volunteers/api/volunteers.api'
import { useVolunteerTableRowsForGroup } from '@/features/volunteers/hooks/volunteers.hooks'
import { isValidPhoneNumber, transformToE164 } from '@/shared/lib/utils/phone.utils'
import { NO_ML_APPROVED_VALUE } from '@/features/stm/components/forms/ContactUserSelect'
import { NO_EMAIL_AVAILABLE_OPTION, isNoEmailPlaceholderValue } from '@/features/volunteers/schemas/volunteers.schema'
import { UserRole, TEAM_LEADER_ROLE_ID, VOLUNTEER_ROLE_ID } from '@/shared/constants/user-roles'
import { format, parseISO } from 'date-fns'
import { inferTripTypeFromProjectId } from '@/features/stm/lib/project-id-trip-type'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Trash2 } from 'lucide-react'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/volunteers/constants/volunteer-tab-links'

const GROUP_CONTACT_ROLES = [UserRole.VOLUNTEER, UserRole.TEAM_LEADER, UserRole.PARTNER]
const NO_DIETARY_VALUES = new Set(['no', 'none', 'na', 'n/a', 'n-a', 'nope'])

function isVolunteerUserRole(user: { role?: { name?: string; id?: number } } | undefined): boolean {
  if (!user?.role) return false
  return user.role.name === UserRole.VOLUNTEER || user.role.id === VOLUNTEER_ROLE_ID
}

function hasMeaningfulDietaryRestriction(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase().replace(/\./g, '')
  if (!normalized) return false
  return !NO_DIETARY_VALUES.has(normalized)
}

function normalizeDietaryRestrictionValue(value: unknown): string | null {
  if (!hasMeaningfulDietaryRestriction(value)) return null
  const cleaned = String(value).trim().replace(/\s+/g, ' ')
  return cleaned.length > 0 ? cleaned : null
}

/**
 * Table-rows API may put dietary text on the user (`dietaryRestrictions`) and/or on `volunteer`.
 * Must not use `nested ?? top` alone: an empty string on `volunteer` is not nullish, so the top-level
 * value would be skipped (same ordering as GroupParticipantsTab: prefer top, then nested).
 */
function dietaryRestrictionFromTableRow(row: {
  dietaryRestrictions?: string | null
  volunteer?: { dietaryRestrictions?: string | null } | null
}): unknown {
  const top = row.dietaryRestrictions
  const vol = row.volunteer?.dietaryRestrictions
  if (hasMeaningfulDietaryRestriction(top)) return top
  if (hasMeaningfulDietaryRestriction(vol)) return vol
  return undefined
}

/**
 * True when `merged` (from all members) includes every comma-separated fragment of the saved/current
 * text but adds more (e.g. saved "Nuts, dairy" vs merged "Nuts, dairy, Cilantro").
 * Avoids overwriting free-form notes that are not reflected in member merge text.
 */
function savedDietaryDetailsIsSubsetOfMemberMerge(savedOrCurrent: string, merged: string): boolean {
  const cur = savedOrCurrent.trim()
  const merg = merged.trim()
  if (!merg || cur === merg) return false
  const parts = cur.split(',').map((s) => s.trim()).filter(Boolean)
  if (parts.length === 0) return merg.length > cur.length
  const mergedLower = merg.toLowerCase()
  const allPartsInMerged = parts.every((p) => {
    const pl = p.toLowerCase()
    return pl.length > 0 && mergedLower.includes(pl)
  })
  return allPartsInMerged && merg.length > cur.length
}

function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const part = dateStr.slice(0, 10)
  return part.length === 10 ? part : ''
}

function buildNoEmailPlaceholder(fullName?: string) {
  const safeName = (fullName || 'volunteer')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'volunteer'
  return `no-email+${safeName}-${Date.now()}@reliefhub.local`
}

type GroupDashboardTabProps = {
  group: Group
}

export function GroupDashboardTab({ group }: GroupDashboardTabProps) {
  const pathname = usePathname()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { profile, session } = useSession()
  const { regionSlug } = useRegion()
  const updateGroup = useStmUpdateGroup()
  const deleteGroup = useStmDeleteGroup()
  const { data: groupMembersData } = useVolunteerTableRowsForGroup(group.id)

  const isGroupRoleUser = Boolean(
    session &&
      GROUP_CONTACT_ROLES.includes(session.role as UserRole)
  )
  const isGroupLeaderView = Boolean(
    isGroupRoleUser &&
      group.contactUserId != null &&
      Number(session?.sub) === group.contactUserId
  )
  const isTeamLeaderGroupMember = Boolean(
    session?.sub != null &&
      groupMembersData?.success &&
      groupMembersData.data.users.some((u) => {
        const roleName = ((u.role as { name?: string } | undefined)?.name ?? '').replace(/\s+/g, '').toLowerCase()
        return Number(u.id) === Number(session.sub) && roleName === UserRole.TEAM_LEADER.toLowerCase()
      })
  )
  const isGroupScopedLeaderView = isGroupLeaderView || isTeamLeaderGroupMember
  // Volunteers can view notes but cannot add/edit notes even if they are group contact.
  const canAddGroupLeaderNote =
    isGroupScopedLeaderView && session?.role !== UserRole.VOLUNTEER
  const isGroupMemberReadOnly = isGroupRoleUser && !canAddGroupLeaderNote

  const [contactUserId, setContactUserId] = useState<number | null>(group.contactUserId ?? null)
  const [remoteGroupLeader, setRemoteGroupLeader] = useState(!!group.remote_group_leader)
  const [newContact, setNewContact] = useState<NewContactState>({ name: '', email: '', phone: '' })
  const [selectedContactEmail, setSelectedContactEmail] = useState(group.contact_email ?? '')
  const [selectedContactPhone, setSelectedContactPhone] = useState(group.contact_phone ?? '')
  const [selectedContactBaseline, setSelectedContactBaseline] = useState<{
    userId: number | null
    email: string
    phone: string
  }>({
    userId: group.contactUserId ?? null,
    email: group.contact_email ?? '',
    phone: group.contact_phone ?? '',
  })
  const [churchOrg, setChurchOrg] = useState(group.church_org ?? '')

  const [groupName, setGroupName] = useState(group.group_name)
  const [projectId, setProjectId] = useState(group.project_id ?? '')
  const [tripType, setTripType] = useState<TripType | null>(group.trip_type)
  const projectIdRef = useRef(projectId)
  const tripTypeRef = useRef(tripType)
  projectIdRef.current = projectId
  tripTypeRef.current = tripType

  const handleProjectIdChange = useCallback((next: string) => {
    const prevPid = projectIdRef.current
    const prevInf = inferTripTypeFromProjectId(prevPid)
    const nextInf = inferTripTypeFromProjectId(next)
    setProjectId(next)
    if (!nextInf) return
    if (nextInf !== prevInf) {
      setTripType(nextInf)
      return
    }
    const tt = tripTypeRef.current
    if (tt === prevInf || tt === null) setTripType(nextInf)
  }, [])
  const [participants, setParticipants] = useState(String(group.participants))
  const [startDate, setStartDate] = useState(toDateInputValue(group.start_date))
  const [endDate, setEndDate] = useState(toDateInputValue(group.end_date))
  const [status, setStatus] = useState<GroupStatus>(group.status)
  const [retention, setRetention] = useState<GroupRetention | null>(
    group.retention_group === 'Retention' ? 'Return' : (group.retention_group ?? null)
  )

  const [notes, setNotes] = useState(group.notes ?? '')
  const [groupLeaderNewNote, setGroupLeaderNewNote] = useState('')
  const [taggedNoteVolunteers, setTaggedNoteVolunteers] = useState<TaggedVolunteer[]>([])

  const [leaderAssigned, setLeaderAssigned] = useState(group.leader_assigned)
  const [leaderName, setLeaderName] = useState(group.leader_name ?? '')
  const [leaderEmail, setLeaderEmail] = useState(group.leader_email ?? '')
  const [leaderPhone, setLeaderPhone] = useState(group.leader_phone ?? '')
  const [missionTripLeaderUserId, setMissionTripLeaderUserId] = useState<number | null>(
    group.leader_name === 'No ML Approved' ? NO_ML_APPROVED_VALUE : null
  )
  const [pretripInfoGiven, setPretripInfoGiven] = useState(group.pretrip_info_given)
  const [depositReceived, setDepositReceived] = useState(group.deposit_received ?? false)
  const [backgroundCheckComplete, setBackgroundCheckComplete] = useState(
    group.background_check_complete ?? false
  )
  const [fullPaymentReceived, setFullPaymentReceived] = useState(
    group.full_payment_received ?? false
  )
  const [postTripInfo, setPostTripInfo] = useState(group.post_trip_info ?? false)
  const [dietaryRestrictions, setDietaryRestrictions] = useState(
    group.dietary_restrictions ?? false
  )
  const [dietaryRestrictionsDetails, setDietaryRestrictionsDetails] = useState(
    group.dietary_restrictions_details ?? ''
  )

  const effectiveRegionForWeeks = group.regionId ?? group.region ?? null
  const calendarQueryStart = useMemo(() => {
    const s = (startDate || toDateInputValue(group.start_date) || '').slice(0, 10)
    if (!s) return format(new Date(), 'yyyy-MM-01')
    return `${s.slice(0, 7)}-01`
  }, [startDate, group.start_date])
  const calendarQueryMonths = useMemo(() => {
    const s = (startDate || toDateInputValue(group.start_date) || '').slice(0, 10)
    const e = (endDate || toDateInputValue(group.end_date) || '').slice(0, 10)
    if (!s || !e) return 12
    const a = parseISO(s)
    const b = parseISO(e)
    const months =
      (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1
    return Math.min(Math.max(months, 1), 24)
  }, [startDate, endDate, group.start_date, group.end_date])

  const { data: weeksForConflict } = useStmWeeksWithStats(
    effectiveRegionForWeeks,
    calendarQueryStart,
    calendarQueryMonths
  )

  const hardBlockConflictCount = useMemo(() => {
    const ws = (startDate || toDateInputValue(group.start_date) || '').slice(0, 10)
    const we = (endDate || toDateInputValue(group.end_date) || '').slice(0, 10)
    if (!ws || !we || !weeksForConflict?.length) return 0
    return weeksForConflict.filter(
      (w) => doesOverlap(ws, we, w.week_start, w.week_end) && w.hasHardBlock
    ).length
  }, [startDate, endDate, group.start_date, group.end_date, weeksForConflict])

  const [showSuccessBar, setShowSuccessBar] = useState(false)
  /** After we show success, don't clear the bar until group has refetched (isDirty becomes false). */
  const justShowSuccessRef = useRef(false)
  const skipGroupLeaderPromoteConfirmRef = useRef(false)
  const [promoteLeaderDialogOpen, setPromoteLeaderDialogOpen] = useState(false)
  const lastAutoDietaryDetailsRef = useRef('')
  const prevDietaryRestrictionsRef = useRef(group.dietary_restrictions ?? false)

  const { data: teamLeadersData } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
      return res.success && res.data ? res.data : []
    },
  })

  // When group has a contact but no church_org saved, fill Church/Organization from the user's profile
  useEffect(() => {
    if (!Array.isArray(teamLeadersData) || contactUserId == null || (group.church_org ?? '') !== '') return
    const match = teamLeadersData.find((u: { id?: number }) => u.id === contactUserId) as { volunteer?: { organizationName?: string } } | undefined
    if (match?.volunteer?.organizationName) setChurchOrg(match.volunteer.organizationName)
  }, [teamLeadersData, contactUserId, group.church_org])

  useEffect(() => {
    setContactUserId(group.contactUserId ?? null)
    setRemoteGroupLeader(!!group.remote_group_leader)
    setSelectedContactEmail(group.contact_email ?? '')
    setSelectedContactPhone(group.contact_phone ?? '')
    setSelectedContactBaseline({
      userId: group.contactUserId ?? null,
      email: group.contact_email ?? '',
      phone: group.contact_phone ?? '',
    })
    setChurchOrg(group.church_org ?? '')
    setNewContact({
      name: '',
      email: group.contactUserId == null ? NO_EMAIL_AVAILABLE_OPTION : '',
      phone: '',
    })
    setGroupName(group.group_name)
    setProjectId(group.project_id ?? '')
    setTripType(group.trip_type)
    setParticipants(String(group.participants))
    setStartDate(toDateInputValue(group.start_date))
    setEndDate(toDateInputValue(group.end_date))
    setStatus(group.status)
    setRetention(group.retention_group === 'Retention' ? 'Return' : (group.retention_group ?? null))
    setNotes(group.notes ?? '')
    setLeaderAssigned(group.leader_assigned)
    setLeaderName(group.leader_name ?? '')
    setLeaderEmail(group.leader_email ?? '')
    setLeaderPhone(group.leader_phone ?? '')
    let nextMissionTripLeaderUserId: number | null = null
    let missionLeaderResolutionSource: 'no_ml' | 'email_match' | 'none' = 'none'
    if (group.leader_name === 'No ML Approved') {
      nextMissionTripLeaderUserId = NO_ML_APPROVED_VALUE
      missionLeaderResolutionSource = 'no_ml'
    } else if (
      group.leader_email?.trim() &&
      Array.isArray(teamLeadersData)
    ) {
      const match = (teamLeadersData as { id?: number; email?: string }[]).find(
        (u) =>
          u.email?.trim().toLowerCase() === group.leader_email!.trim().toLowerCase()
      )
      if (match?.id != null) {
        nextMissionTripLeaderUserId = match.id
        missionLeaderResolutionSource = 'email_match'
      }
    }
    setMissionTripLeaderUserId(nextMissionTripLeaderUserId)
    setPretripInfoGiven(group.pretrip_info_given)
    setDepositReceived(group.deposit_received ?? false)
    setBackgroundCheckComplete(group.background_check_complete ?? false)
    setFullPaymentReceived(group.full_payment_received ?? false)
    setPostTripInfo(group.post_trip_info ?? false)
    setDietaryRestrictions(group.dietary_restrictions ?? false)
    setDietaryRestrictionsDetails(group.dietary_restrictions_details ?? '')
    prevDietaryRestrictionsRef.current = group.dietary_restrictions ?? false
    lastAutoDietaryDetailsRef.current = (group.dietary_restrictions_details ?? '').trim()
  }, [
    group.id,
    group.contactUserId,
    group.remote_group_leader,
    group.contact_email,
    group.contact_phone,
    group.church_org,
    group.group_name,
    group.project_id,
    group.trip_type,
    group.participants,
    group.start_date,
    group.end_date,
    group.status,
    group.retention_group,
    group.notes,
    group.leader_assigned,
    group.leader_name,
    group.leader_email,
    group.leader_phone,
    group.pretrip_info_given,
    group.deposit_received,
    group.background_check_complete,
    group.full_payment_received,
    group.post_trip_info,
    group.dietary_restrictions,
    group.dietary_restrictions_details,
    teamLeadersData,
  ])

  const dietaryRestrictionsFromGroupMembers = useMemo(() => {
    const rows = groupMembersData?.success ? groupMembersData.data.users : undefined
    const unique: string[] = []
    const seen = new Set<string>()
    const appendRestriction = (raw: unknown) => {
      const normalized = normalizeDietaryRestrictionValue(raw)
      if (!normalized) return
      const key = normalized.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      unique.push(normalized)
    }
    if (Array.isArray(rows)) {
      rows.forEach((row: any) => {
        appendRestriction(dietaryRestrictionFromTableRow(row))
      })
    }
    // Fallback for newly-created groups where only Group Leader is set and may not yet be in members list.
    if (group.contactUserId != null && Array.isArray(teamLeadersData)) {
      const groupLeader = (teamLeadersData as Array<{
        id?: number
        dietaryRestrictions?: string | null
        volunteer?: { dietaryRestrictions?: string | null } | null
      }>).find((u) => Number(u?.id) === Number(group.contactUserId))
      appendRestriction(dietaryRestrictionFromTableRow(groupLeader ?? {}))
    }
    return unique.join(', ')
  }, [groupMembersData, group.contactUserId, teamLeadersData])
  const registeredParticipantsCount = groupMembersData?.success
    ? groupMembersData.data.total
    : (group.registered_participants ?? 0)

  const derivedDietaryRestrictionsChecked = dietaryRestrictionsFromGroupMembers.trim().length > 0

  useEffect(() => {
    if (!derivedDietaryRestrictionsChecked) return
    if (!dietaryRestrictions) {
      setDietaryRestrictions(true)
    }
  }, [derivedDietaryRestrictionsChecked, dietaryRestrictions])

  // Auto-fill dietary details from group members once dietary restrictions is enabled.
  // Keep user edits intact by only replacing blank or previously auto-generated values.
  useEffect(() => {
    const justEnabledDietary =
      !prevDietaryRestrictionsRef.current && dietaryRestrictions
    prevDietaryRestrictionsRef.current = dietaryRestrictions

    if (!dietaryRestrictions || !dietaryRestrictionsFromGroupMembers) return
    const current = (dietaryRestrictionsDetails ?? '').trim()
    const lastAuto = (lastAutoDietaryDetailsRef.current ?? '').trim()
    const hasExistingAutoValue = lastAuto.length > 0
    const groupSavedDetails = (group.dietary_restrictions_details ?? '').trim()
    const shouldInitialHydrateFromMembers =
      current.length === 0 &&
      lastAuto.length === 0 &&
      groupSavedDetails.length === 0
    /** Saved group text matches server, but member profiles now imply a richer merged string. */
    const shouldSyncStaleSavedDetails =
      current === groupSavedDetails &&
      groupSavedDetails.length > 0 &&
      savedDietaryDetailsIsSubsetOfMemberMerge(groupSavedDetails, dietaryRestrictionsFromGroupMembers)
    const shouldAutoFill =
      (justEnabledDietary && current.length === 0) ||
      shouldInitialHydrateFromMembers ||
      (groupSavedDetails.length === 0 && hasExistingAutoValue && current === lastAuto) ||
      shouldSyncStaleSavedDetails
    if (!shouldAutoFill) return
    if (current === dietaryRestrictionsFromGroupMembers) {
      lastAutoDietaryDetailsRef.current = dietaryRestrictionsFromGroupMembers
      return
    }
    setDietaryRestrictionsDetails(dietaryRestrictionsFromGroupMembers)
    lastAutoDietaryDetailsRef.current = dietaryRestrictionsFromGroupMembers
  }, [
    group.id,
    group.dietary_restrictions_details,
    dietaryRestrictions,
    dietaryRestrictionsFromGroupMembers,
    dietaryRestrictionsDetails,
  ])

  const effectiveDietaryRestrictionsBaseline =
    derivedDietaryRestrictionsChecked || (group.dietary_restrictions ?? false)

  const groupSavedDetailsTrim = (group.dietary_restrictions_details ?? '').trim()
  const mergedDietaryFromMembersTrim = (dietaryRestrictionsFromGroupMembers ?? '').trim()
  /** When DB text lags member profiles, merged list is the canonical display; baseline must match so auto-fill is not "dirty". */
  const effectiveDietaryDetailsBaseline =
    groupSavedDetailsTrim.length > 0 &&
    mergedDietaryFromMembersTrim.length > 0 &&
    savedDietaryDetailsIsSubsetOfMemberMerge(groupSavedDetailsTrim, dietaryRestrictionsFromGroupMembers)
      ? dietaryRestrictionsFromGroupMembers
      : groupSavedDetailsTrim.length > 0
        ? (group.dietary_restrictions_details ?? '')
        : dietaryRestrictions &&
            mergedDietaryFromMembersTrim.length > 0
          ? dietaryRestrictionsFromGroupMembers
          : (group.dietary_restrictions_details ?? '')

  const newContactNameTrimmed = newContact.name.trim()
  const newContactEmailTrimmed = newContact.email.trim()
  const newContactPhoneTrimmed = newContact.phone.trim()
  const isNoEmailOnlyAutoFill =
    contactUserId == null &&
    newContactNameTrimmed.length === 0 &&
    newContactPhoneTrimmed.length === 0 &&
    isNoEmailPlaceholderValue(newContactEmailTrimmed)
  const hasNewContactDraft =
    contactUserId == null &&
    (
      newContactNameTrimmed.length > 0 ||
      newContactPhoneTrimmed.length > 0 ||
      (newContactEmailTrimmed.length > 0 && !isNoEmailOnlyAutoFill)
    )
  const hasSelectedContactEdits =
    contactUserId != null &&
    !isGroupRoleUser &&
    selectedContactBaseline.userId === contactUserId &&
    (
      selectedContactEmail.trim() !== selectedContactBaseline.email ||
      selectedContactPhone.trim() !== selectedContactBaseline.phone
    )

  const hasGeneralEdits =
    hasNewContactDraft ||
    hasSelectedContactEdits ||
    contactUserId !== (group.contactUserId ?? null) ||
    remoteGroupLeader !== !!group.remote_group_leader ||
    churchOrg !== (group.church_org ?? '') ||
    groupName !== group.group_name ||
    projectId !== (group.project_id ?? '') ||
    (tripType ?? null) !== (group.trip_type ?? null) ||
    participants !== String(group.participants) ||
    startDate !== toDateInputValue(group.start_date) ||
    endDate !== toDateInputValue(group.end_date) ||
    status !== group.status ||
    retention !== (group.retention_group === 'Retention' ? 'Return' : (group.retention_group ?? null)) ||
    notes !== (group.notes ?? '') ||
    leaderAssigned !== group.leader_assigned ||
    leaderName !== (group.leader_name ?? '') ||
    leaderEmail !== (group.leader_email ?? '') ||
    leaderPhone !== (group.leader_phone ?? '') ||
    pretripInfoGiven !== group.pretrip_info_given ||
    depositReceived !== (group.deposit_received ?? false) ||
    backgroundCheckComplete !== (group.background_check_complete ?? false) ||
    fullPaymentReceived !== (group.full_payment_received ?? false) ||
    postTripInfo !== (group.post_trip_info ?? false) ||
    dietaryRestrictions !== effectiveDietaryRestrictionsBaseline ||
    dietaryRestrictionsDetails !== effectiveDietaryDetailsBaseline
  const hasGroupLeaderNewNote = groupLeaderNewNote.trim().length > 0
  const hasTaggedNotes = taggedNoteVolunteers.length > 0

  const isDirty =
    (!isGroupRoleUser && (hasGeneralEdits || hasTaggedNotes)) ||
    (canAddGroupLeaderNote && hasGroupLeaderNewNote)

  useEffect(() => {
    if (!isDirty) justShowSuccessRef.current = false
    if (isDirty && showSuccessBar && !justShowSuccessRef.current) setShowSuccessBar(false)
  }, [isDirty, showSuccessBar])

  const handleStickySave = useCallback(async () => {
    if (isGroupMemberReadOnly || !isDirty) return
    if (canAddGroupLeaderNote) {
      const notesForSave = groupLeaderNewNote.trim()
        ? `${notes}${notes.trim() ? '\n' : ''}${groupLeaderNewNote.trim()}`
        : notes
      const taggedUserIds = taggedNoteVolunteers.map((v) => v.id)
      updateGroup.mutate(
        {
          id: group.id,
          notes: notesForSave || null,
          ...(taggedUserIds.length > 0 && { taggedUserIds }),
          ...(taggedUserIds.length > 0 && pathname && {
            actionPath: buildUrl(pathname, { tab: 'dashboard' }),
          }),
          ...(profile?.fullname && { senderName: profile.fullname }),
        },
        {
          onSuccess: () => {
            justShowSuccessRef.current = true
            setShowSuccessBar(true)
            setTaggedNoteVolunteers([])
            setGroupLeaderNewNote('')
          },
          onError: (e: Error) => toast.error(e?.message ?? 'Failed to save'),
        }
      )
      return
    }
    if (!skipGroupLeaderPromoteConfirmRef.current && contactUserId != null) {
      const match = Array.isArray(teamLeadersData)
        ? (teamLeadersData as Array<{ id?: number; role?: { name?: string; id?: number } }>).find(
            (u) => Number(u.id) === Number(contactUserId),
          )
        : undefined
      if (isVolunteerUserRole(match)) {
        setPromoteLeaderDialogOpen(true)
        return
      }
    }
    skipGroupLeaderPromoteConfirmRef.current = false
    const participantsNum = parseInt(participants, 10)
    if (isNaN(participantsNum) || participantsNum < 0) {
      toast.error('Expected Participants must be 0 or greater')
      return
    }
    if (!startDate.trim() || !endDate.trim()) {
      toast.error('Trip Dates (start and end) are required.')
      return
    }
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date must be on or before end date')
      return
    }
    if (hardBlockConflictCount > 0) {
      toast.error(
        `This trip overlaps ${hardBlockConflictCount} blocked week(s). The base is unavailable during this time.`
      )
      return
    }

    let effectiveContactUserId = contactUserId
    const newContactName = newContact.name.trim()
    const rawNewContactEmail = newContact.email.trim()
    const noEmailSelected = isNoEmailPlaceholderValue(rawNewContactEmail) || rawNewContactEmail.toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase()
    const phone = newContact.phone.trim()
    if (effectiveContactUserId == null && newContactName && !rawNewContactEmail) {
      toast.error('Please enter a group leader email or choose No Email Available.')
      return
    }
    if (effectiveContactUserId == null && newContactName && rawNewContactEmail && !phone) {
      toast.error('Please enter a valid group leader phone number.')
      return
    }
    if (effectiveContactUserId == null && newContactName && rawNewContactEmail && phone) {
      const email = noEmailSelected ? buildNoEmailPlaceholder(newContactName) : rawNewContactEmail
      const name = newContact.name.trim()
      if (!noEmailSelected && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid group leader email address.')
        return
      }
      if (!isValidPhoneNumber(phone)) {
        toast.error('Please enter a valid group leader phone number.')
        return
      }
      const existingByEmail = Array.isArray(teamLeadersData)
        ? (teamLeadersData as { email?: string; fullname?: string }[]).find(
            (u) => u.email && u.email.trim().toLowerCase() === email.toLowerCase()
          )
        : null
      if (existingByEmail && existingByEmail.fullname?.trim().toLowerCase() !== name.trim().toLowerCase()) {
        toast.error('This email is already associated with another volunteer. Please select that person from the list or use a different email.')
        return
      }
      try {
        const fromDate = startDate ? new Date(startDate) : new Date()
        const toDate = endDate ? new Date(endDate) : new Date()
        const createRes = await createVolunteer({
          fullName: name,
          email,
          phone: transformToE164(phone),
          role: TEAM_LEADER_ROLE_ID,
          servePeriods: [
            { from: fromDate, to: toDate, regionId: group.regionId ?? 0 },
          ],
        } as any)
        if (!createRes.success) {
          toast.error(createRes.message ?? 'Failed to create group leader.')
          return
        }
        const newId = createRes.data?.user?.id
        if (newId == null) {
          toast.error('Group leader was created but could not be linked.')
          return
        }
        effectiveContactUserId = newId
        queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
        toast.success('Group leader added to the system.')
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to create group leader.')
        return
      }
    }

    if (effectiveContactUserId != null && !isGroupRoleUser && hasSelectedContactEdits) {
      const email = selectedContactEmail.trim()
      const phone = selectedContactPhone.trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid group leader email address.')
        return
      }
      if (phone && !isValidPhoneNumber(phone)) {
        toast.error('Please enter a valid group leader phone number.')
        return
      }
      const updateRes = await updateUser(effectiveContactUserId, {
        email,
        phone: phone || undefined,
      })
      if (!updateRes.success) {
        toast.error(updateRes.message ?? 'Failed to update group leader contact info.')
        return
      }
      queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
    }

    let effectiveLeaderName = leaderName
    let effectiveLeaderEmail = leaderEmail || null
    let effectiveLeaderPhone = leaderPhone || null
    if (leaderAssigned) {
      if (missionTripLeaderUserId === NO_ML_APPROVED_VALUE) {
        effectiveLeaderName = 'No ML Approved'
        effectiveLeaderEmail = null
        effectiveLeaderPhone = null
      } else if (
        missionTripLeaderUserId == null &&
        leaderName.trim() &&
        leaderEmail.trim() &&
        leaderPhone.trim()
      ) {
        const rawLeaderEmail = leaderEmail.trim()
        const noMissionLeaderEmailSelected =
          isNoEmailPlaceholderValue(rawLeaderEmail) ||
          rawLeaderEmail.toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase()
        const emailForCreate = noMissionLeaderEmailSelected
          ? buildNoEmailPlaceholder(leaderName.trim())
          : rawLeaderEmail
        if (!noMissionLeaderEmailSelected && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawLeaderEmail)) {
          toast.error('Please enter a valid mission trip leader email address.')
          return
        }
        if (leaderPhone.trim() && !isValidPhoneNumber(leaderPhone.trim())) {
          toast.error('Please enter a valid mission trip leader phone number.')
          return
        }
        const existingMlByEmail = Array.isArray(teamLeadersData)
          ? (teamLeadersData as { email?: string; fullname?: string }[]).find(
              (u) => u.email && u.email.trim().toLowerCase() === emailForCreate.toLowerCase()
            )
          : null
        if (existingMlByEmail && existingMlByEmail.fullname?.trim().toLowerCase() !== leaderName.trim().toLowerCase()) {
          toast.error('This email is already associated with another volunteer. Please select that person from the list or use a different email.')
          return
        }
        const groupAlreadyHasThisLeader =
          !noMissionLeaderEmailSelected &&
          group.leader_email?.trim() &&
          rawLeaderEmail &&
          group.leader_email.trim().toLowerCase() === rawLeaderEmail.toLowerCase()
        if (groupAlreadyHasThisLeader || existingMlByEmail) {
          effectiveLeaderName = existingMlByEmail?.fullname?.trim() ?? leaderName.trim()
          effectiveLeaderEmail = existingMlByEmail?.email?.trim() ?? (noMissionLeaderEmailSelected ? NO_EMAIL_AVAILABLE_OPTION : rawLeaderEmail)
          effectiveLeaderPhone = existingMlByEmail
            ? (transformToE164((existingMlByEmail as { phone?: string }).phone ?? '') || leaderPhone.trim())
            : transformToE164(leaderPhone.trim())
        } else {
          try {
            const fromDate = startDate ? new Date(startDate) : new Date()
            const toDate = endDate ? new Date(endDate) : new Date()
            const createRes = await createVolunteer({
              fullName: leaderName.trim(),
              email: emailForCreate,
              phone: leaderPhone.trim() ? transformToE164(leaderPhone.trim()) : undefined,
              role: 4,
              servePeriods: [
                { from: fromDate, to: toDate, regionId: group.regionId ?? 0 },
              ],
            } as any)
            if (!createRes.success) {
              toast.error(createRes.message ?? 'Failed to create mission trip leader.')
              return
            }
            effectiveLeaderName = leaderName.trim()
            effectiveLeaderEmail = noMissionLeaderEmailSelected ? NO_EMAIL_AVAILABLE_OPTION : rawLeaderEmail
            effectiveLeaderPhone = leaderPhone.trim() ? transformToE164(leaderPhone.trim()) : null
            queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
            toast.success('Mission trip leader added to the system.')
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed to create mission trip leader.')
            return
          }
        }
      } else if (missionTripLeaderUserId != null) {
        const selectedMl = Array.isArray(teamLeadersData)
          ? (teamLeadersData as Array<{ id?: number; fullname?: string; email?: string; phone?: string }>).find(
              (u) => u.id === missionTripLeaderUserId
            )
          : undefined
        const resolvedName =
          leaderName.trim() ||
          selectedMl?.fullname?.trim() ||
          group.leader_name?.trim() ||
          ''
        if (!resolvedName) {
          toast.error('Please select a mission trip leader.')
          return
        }

        const email = leaderEmail.trim()
        const phone = leaderPhone.trim()
        const selectedEmail = selectedMl?.email?.trim() ?? ''
        const selectedPhone = selectedMl?.phone
          ? (transformToE164(selectedMl.phone) || selectedMl.phone.trim())
          : ''
        const nextPhone = phone ? (transformToE164(phone) || phone) : ''
        const shouldUpdateSelectedMlProfile =
          !isGroupRoleUser &&
          selectedMl != null &&
          (
            (email.length > 0 && email !== selectedEmail) ||
            (nextPhone.length > 0 && nextPhone !== selectedPhone)
          )

        if (shouldUpdateSelectedMlProfile) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid mission trip leader email address.')
            return
          }
          if (phone && !isValidPhoneNumber(phone)) {
            toast.error('Please enter a valid mission trip leader phone number.')
            return
          }
          const missionLeaderUpdateRes = await updateUser(missionTripLeaderUserId, {
            email,
            phone: phone || undefined,
          })
          if (!missionLeaderUpdateRes.success) {
            toast.error(missionLeaderUpdateRes.message ?? 'Failed to update mission trip leader contact info.')
            return
          }
          queryClient.invalidateQueries({ queryKey: ['volunteers-team-leaders'] })
        }

        effectiveLeaderName = resolvedName
        effectiveLeaderEmail = email || selectedEmail || null
        effectiveLeaderPhone = nextPhone || selectedPhone || null
      } else if (
        missionTripLeaderUserId == null &&
        (!leaderName.trim() || !leaderEmail.trim())
      ) {
        if (leaderName.trim() && !leaderEmail.trim()) {
          toast.error('Please enter a mission trip leader email or choose No Email Available.')
          return
        }
        toast.error('Please select a mission trip leader, choose No ML Approved, or enter name and email.')
        return
      }
    }

    const taggedUserIds = taggedNoteVolunteers.map((v) => v.id)
    const notesForSave =
      !isGroupRoleUser && groupLeaderNewNote.trim()
        ? `${notes}${notes.trim() ? '\n' : ''}${groupLeaderNewNote.trim()}`
        : notes
    const leaderPayload =
      leaderAssigned && missionTripLeaderUserId === NO_ML_APPROVED_VALUE
        ? { leader_name: 'No ML Approved' as const, leader_email: null as string | null, leader_phone: null as string | null }
        : {
            leader_name: effectiveLeaderName || null,
            leader_email: effectiveLeaderEmail,
            leader_phone: effectiveLeaderPhone,
          }
    const payload = {
        id: group.id,
        contactUserId: effectiveContactUserId ?? null,
        remote_group_leader: remoteGroupLeader,
        church_org: churchOrg || null,
        group_name: groupName,
        project_id: projectId || null,
        trip_type: tripType,
        participants: participantsNum,
        start_date: startDate || group.start_date,
        end_date: endDate || group.end_date,
        status,
        retention_group: retention || null,
        notes: notesForSave || null,
        leader_assigned: leaderAssigned,
        ...leaderPayload,
        pretrip_info_given: pretripInfoGiven,
        deposit_received: depositReceived,
        background_check_complete: backgroundCheckComplete,
        full_payment_received: fullPaymentReceived,
        post_trip_info: postTripInfo,
        dietary_restrictions: dietaryRestrictions,
        dietary_restrictions_details: dietaryRestrictions
          ? dietaryRestrictionsDetails || null
          : null,
        ...(taggedUserIds.length > 0 && { taggedUserIds }),
        ...(taggedUserIds.length > 0 && pathname && {
          actionPath: buildUrl(pathname, { tab: 'dashboard' }),
        }),
        ...(profile?.fullname && { senderName: profile.fullname }),
      }
    updateGroup.mutate(
      payload,
      {
        onSuccess: () => {
          justShowSuccessRef.current = true
          setShowSuccessBar(true)
          setTaggedNoteVolunteers([])
          setGroupLeaderNewNote('')
        },
        onError: (e: Error) => {
          toast.error(e?.message ?? 'Failed to save')
        },
      }
    )
  }, [
    isDirty,
    group.id,
    group.regionId,
    group.start_date,
    group.end_date,
    group.leader_name,
    group.leader_email,
    contactUserId,
    remoteGroupLeader,
    hasSelectedContactEdits,
    selectedContactEmail,
    selectedContactPhone,
    newContact,
    teamLeadersData,
    churchOrg,
    groupName,
    projectId,
    tripType,
    participants,
    startDate,
    endDate,
    status,
    retention,
    notes,
    leaderAssigned,
    leaderName,
    leaderEmail,
    leaderPhone,
    missionTripLeaderUserId,
    pretripInfoGiven,
    depositReceived,
    backgroundCheckComplete,
    fullPaymentReceived,
    postTripInfo,
    dietaryRestrictions,
    dietaryRestrictionsDetails,
    taggedNoteVolunteers,
    pathname,
    profile?.fullname,
    updateGroup,
    queryClient,
    isGroupRoleUser,
    canAddGroupLeaderNote,
    isGroupMemberReadOnly,
    groupLeaderNewNote,
    hardBlockConflictCount,
  ])

  const isSubmitting = updateGroup.isPending
  const handleDeleteGroup = useCallback(() => {
    deleteGroup.mutate(group.id, {
      onSuccess: () => {
        const href = regionSlug
          ? buildRegionUrl(regionSlug, '/volunteers', { [SEARCH_PARAMS_TAB_KEY]: 'groups' })
          : `/volunteers?${SEARCH_PARAMS_TAB_KEY}=groups`
        router.push(href)
      },
    })
  }, [deleteGroup, group.id, regionSlug, router])

  const barVisible = !isGroupMemberReadOnly && (isDirty || showSuccessBar)

  return (
    <div className={barVisible ? 'pb-14 md:pb-12' : undefined}>
      <Dialog open={promoteLeaderDialogOpen} onOpenChange={setPromoteLeaderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch this person to Team Leader?</DialogTitle>
            <DialogDescription>
              Group Leaders use the Team Leader role in ReliefHub. Saving this group will change this Volunteer to Team Leader. Administrators and Base Leaders are not changed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPromoteLeaderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                skipGroupLeaderPromoteConfirmRef.current = true
                setPromoteLeaderDialogOpen(false)
                void handleStickySave()
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!isGroupRoleUser && (
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleDeleteGroup}
            disabled={deleteGroup.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete Group
          </Button>
        </div>
      )}
      <Accordion
        type="multiple"
        defaultValue={['Group Leader', 'Overall Status', 'Notes', 'Checklist']}
        className="space-y-4"
      >
        <AccordionItem value="Group Leader" className="bg-muted rounded p-4 border">
          <AccordionTrigger className="py-0 p-1">
            <h2 className="text-xl font-semibold">Group Leader</h2>
          </AccordionTrigger>
          <AccordionContent className="pb-0 p-1">
            <GroupLeaderCard
              group={group}
              contactUserId={contactUserId}
              contactEmail={group.contact_email ?? ''}
              contactPhone={group.contact_phone ?? ''}
              selectedContactEmail={selectedContactEmail}
              selectedContactPhone={selectedContactPhone}
              newContact={newContact}
              onNewContactChange={setNewContact}
              onContactUserIdChange={setContactUserId}
              onSelectedContactEmailChange={setSelectedContactEmail}
              onSelectedContactPhoneChange={setSelectedContactPhone}
              onSelectedContactInfoChange={(info) => {
                setSelectedContactEmail(info.email ?? '')
                setSelectedContactPhone(info.phone ?? '')
                setSelectedContactBaseline({
                  userId: info.userId,
                  email: info.email ?? '',
                  phone: info.phone ?? '',
                })
              }}
              remoteGroupLeader={remoteGroupLeader}
              onRemoteGroupLeaderChange={setRemoteGroupLeader}
              readOnly={isGroupRoleUser}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="Overall Status" className="bg-muted rounded p-4 border">
          <AccordionTrigger className="py-0 p-1">
            <h2 className="text-xl font-semibold">Overall Status</h2>
          </AccordionTrigger>
          <AccordionContent className="pb-0 p-1">
            <GroupOverallStatusCard
              group={group}
              groupName={groupName}
              churchOrg={churchOrg}
              projectId={projectId}
              tripType={tripType}
              participants={participants}
              registeredParticipants={registeredParticipantsCount}
              startDate={startDate}
              endDate={endDate}
              status={status}
              retention={retention}
              onGroupNameChange={setGroupName}
              onChurchOrgChange={setChurchOrg}
              onProjectIdChange={handleProjectIdChange}
              onTripTypeChange={setTripType}
              onParticipantsChange={setParticipants}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onStatusChange={setStatus}
              onRetentionChange={setRetention}
              readOnly={isGroupRoleUser}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="Notes" className="bg-muted rounded p-4 border">
          <AccordionTrigger className="py-0 p-1">
            <h2 className="text-xl font-semibold">Notes</h2>
          </AccordionTrigger>
          <AccordionContent className="pb-0 p-1">
            <GroupNotesCard
              group={group}
              notes={notes}
              onNotesChange={setNotes}
              taggedVolunteers={taggedNoteVolunteers}
              onTaggedChange={setTaggedNoteVolunteers}
              readOnly={isGroupMemberReadOnly}
              readOnlyForGroupLeader={canAddGroupLeaderNote}
              groupLeaderNewNote={groupLeaderNewNote}
              onGroupLeaderNewNoteChange={setGroupLeaderNewNote}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="Checklist" className="bg-muted rounded p-4 border">
          <AccordionTrigger className="py-0 p-1">
            <h2 className="text-xl font-semibold">Checklist</h2>
          </AccordionTrigger>
          <AccordionContent className="pb-0 p-1">
            <GroupChecklistCard
              leaderAssigned={leaderAssigned}
              leaderName={leaderName}
              leaderEmail={leaderEmail}
              leaderPhone={leaderPhone}
              missionTripLeaderUserId={missionTripLeaderUserId}
              onMissionTripLeaderUserIdChange={setMissionTripLeaderUserId}
              pretripInfoGiven={pretripInfoGiven}
              depositReceived={depositReceived}
              backgroundCheckComplete={backgroundCheckComplete}
              fullPaymentReceived={fullPaymentReceived}
              postTripInfo={postTripInfo}
              dietaryRestrictions={dietaryRestrictions}
              dietaryRestrictionsDetails={dietaryRestrictionsDetails}
              onLeaderAssignedChange={setLeaderAssigned}
              onLeaderNameChange={setLeaderName}
              onLeaderEmailChange={setLeaderEmail}
              onLeaderPhoneChange={setLeaderPhone}
              onPretripInfoGivenChange={setPretripInfoGiven}
              onDepositReceivedChange={setDepositReceived}
              onBackgroundCheckCompleteChange={setBackgroundCheckComplete}
              onFullPaymentReceivedChange={setFullPaymentReceived}
              onPostTripInfoChange={setPostTripInfo}
              onDietaryRestrictionsChange={setDietaryRestrictions}
              onDietaryRestrictionsDetailsChange={setDietaryRestrictionsDetails}
              readOnly={isGroupMemberReadOnly}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <StickySaveBar
        isVisible={barVisible}
        isSuccess={showSuccessBar}
        isSubmitting={isSubmitting}
        onSave={handleStickySave}
      />
    </div>
  )
}
